export type CaseSection = {
  heading: string;
  paragraphs: string[];
  list?: string[];
};

export type UseCase = {
  slug: string;
  title: string;
  short: string;
  description: string;
  updatedAt: string;
  intro: string;
  sections: CaseSection[];
};

export const USE_CASES: UseCase[] = [
  {
    slug: "viagem-pessoal",
    title: "Planejar o orçamento de uma viagem de carro",
    short: "Viagem pessoal: quanto custa ir e voltar, e como dividir entre os passageiros.",
    description:
      "Como estimar o orçamento de uma viagem de carro: combustível, pedágio, ida e volta, paradas no caminho e divisão do custo entre os passageiros.",
    updatedAt: "2026-09-15",
    intro:
      "Antes de uma viagem de férias ou de fim de semana, a pergunta é sempre a mesma: quanto vai custar chegar lá e voltar? Este roteiro monta o orçamento do deslocamento em poucos minutos.",
    sections: [
      {
        heading: "Levante os dados antes de calcular",
        paragraphs: [
          "Você precisa de quatro informações: de onde sai, para onde vai, quanto o carro faz por litro e quanto custa o litro na sua região. Com isso já dá para chegar a um número confiável de combustível; o pedágio o sistema estima a partir das praças da rota.",
        ],
        list: [
          "Origem e destino (CEP, cidade ou endereço completo);",
          "Consumo real do veículo em km/l, medido em estrada;",
          "Preço do litro que você vai pagar de fato;",
          "Se a viagem é só ida ou ida e volta.",
        ],
      },
      {
        heading: "Monte a simulação",
        paragraphs: [
          "Informe origem e destino, marque ida e volta, escolha carro ou moto e preencha combustível. Se a viagem tem escalas — um almoço em outra cidade, a casa de um parente no caminho — adicione as paradas antes de calcular: elas mudam distância e praças.",
          "O resultado separa ida e volta, mostra distância, tempo, combustível e pedágio de cada sentido e fecha com o custo total da viagem.",
        ],
      },
      {
        heading: "Exemplo: fim de semana na praia",
        paragraphs: [
          "Ida de 210 km e volta de 210 km, carro a 12 km/l, gasolina a R$ 5,99 e R$ 58,40 de pedágio na viagem redonda. O combustível fica em 35 litros, R$ 209,65. O deslocamento total sai por R$ 268,05, ou cerca de R$ 0,64 por quilômetro.",
          "Com quatro pessoas no carro, são R$ 67,01 por passageiro na viagem completa — número justo para dividir antes de sair.",
        ],
      },
      {
        heading: "O que o orçamento não cobre",
        paragraphs: [
          "A estimativa cobre o deslocamento. Hospedagem, alimentação, estacionamento e eventuais imprevistos ficam fora. Some uma reserva de 10% a 15% sobre o valor do deslocamento para variações de preço do combustível e desvios de rota.",
        ],
      },
    ],
  },
  {
    slug: "deslocamento-profissional",
    title: "Cobrar corretamente um deslocamento de trabalho",
    short: "Autônomos e prestadores: quanto cobrar por uma visita, instalação ou atendimento fora.",
    description:
      "Como calcular e cobrar um deslocamento de trabalho: custo real da viagem, valor por quilômetro, margem e como apresentar o valor ao cliente.",
    updatedAt: "2026-09-15",
    intro:
      "Técnicos, consultores, fotógrafos, representantes e prestadores em geral enfrentam a mesma dúvida: quanto cobrar para atender um cliente em outra cidade? A resposta começa pelo custo real do trajeto.",
    sections: [
      {
        heading: "Separe custo de preço",
        paragraphs: [
          "Custo é o que sai do seu bolso: combustível e pedágio da ida e da volta, mais o desgaste do veículo. Preço é o que você cobra: custo mais a margem que remunera o seu tempo e os seus custos fixos. Confundir os dois é o erro clássico de quem cobra deslocamento.",
        ],
      },
      {
        heading: "Calcule ida e volta sempre",
        paragraphs: [
          "Em atendimento externo o veículo volta, e o retorno é por sua conta. Toda simulação de deslocamento profissional deve ser feita com ida e volta marcada — caso contrário, você cobra metade do que gasta.",
          "Se o atendimento tem mais de um cliente na mesma saída, monte a rota com as paradas e rateie o custo total entre os atendimentos.",
        ],
      },
      {
        heading: "Exemplo: visita técnica a 130 km",
        paragraphs: [
          "Ida e volta somam 260 km. Carro a 11 km/l com gasolina a R$ 5,99: 23,6 litros, R$ 141,40. Pedágio nos dois sentidos: R$ 38,60. Custo do deslocamento: R$ 180,00, ou R$ 0,69 por quilômetro.",
          "Aplicando um multiplicador de 1,50 para cobrir tempo de estrada, desgaste e margem, o valor cobrado pelo deslocamento fica em R$ 270,00 — separado do valor do serviço em si.",
        ],
        list: [
          "Custo: R$ 180,00 (R$ 0,69/km)",
          "Multiplicador 1,50 → R$ 270,00",
          "Serviço cobrado à parte",
        ],
      },
      {
        heading: "Como apresentar ao cliente",
        paragraphs: [
          "Mostrar a composição do valor reduz objeção: distância, combustível, pedágio e taxa de deslocamento em linhas separadas. Copie o link da simulação e envie junto da proposta — o cliente abre e vê a mesma rota com os mesmos parâmetros.",
        ],
      },
    ],
  },
  {
    slug: "empresas-e-frotas",
    title: "Controlar custos de deslocamento em empresas e frotas",
    short: "Empresas: reembolso de quilometragem, orçamento de rotas e política de deslocamento.",
    description:
      "Como empresas usam distância, combustível, pedágio, custo por km e multiplicador para orçar rotas, definir reembolso de quilometragem e controlar custos de frota.",
    updatedAt: "2026-09-15",
    intro:
      "Em empresas, o deslocamento aparece em três lugares: reembolso a colaboradores, orçamento de rotas de entrega ou visita, e o custo operacional da frota. Os três partem do mesmo cálculo.",
    sections: [
      {
        heading: "Reembolso de quilometragem",
        paragraphs: [
          "A política mais comum paga um valor por quilômetro rodado com veículo próprio. Para definir esse valor com justiça, calcule o custo variável médio das rotas típicas da empresa e acrescente uma parcela de desgaste. Um valor muito baixo empurra o custo para o colaborador; alto demais vira despesa sem controle.",
          "Revise a taxa a cada trimestre ou sempre que o preço do combustível variar de forma relevante.",
        ],
      },
      {
        heading: "Orçamento de rotas e propostas",
        paragraphs: [
          "Para entregas, instalações e atendimentos, simule a rota real com as paradas na ordem prevista. O custo total sai por trecho e por viagem, e o multiplicador aplica a margem comercial de uma vez. O link da simulação pode ser anexado ao orçamento como memória de cálculo.",
        ],
        list: [
          "Simule a rota com todas as paradas na ordem de execução;",
          "Use a categoria e os eixos corretos do veículo da operação;",
          "Aplique o multiplicador definido pela política comercial;",
          "Guarde o link da simulação junto do orçamento aprovado.",
        ],
      },
      {
        heading: "Exemplo: rota semanal de entregas",
        paragraphs: [
          "Utilitário a 9 km/l, diesel a R$ 6,20, rota de 280 km por dia com R$ 42,00 de pedágio. O combustível diário é de 31,1 litros, R$ 192,90; o custo variável diário chega a R$ 234,90, ou R$ 0,84 por quilômetro.",
          "Em cinco dias úteis, R$ 1.174,50 por semana e cerca de R$ 5.100,00 por mês, só de custo variável — número que permite comparar com a alternativa de terceirizar a entrega.",
        ],
      },
      {
        heading: "Governança dos números",
        paragraphs: [
          "Padronize a fonte do cálculo para que todas as áreas usem a mesma base. Documente o consumo adotado por tipo de veículo, o preço de referência do combustível e a data da última revisão. Assim o orçamento de uma área conversa com o de outra, e a diferença entre previsto e realizado vira informação útil.",
        ],
      },
    ],
  },
];

export function getUseCase(slug: string): UseCase | undefined {
  return USE_CASES.find((c) => c.slug === slug);
}
